import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  CircularProgress,
  Box,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Button } from '../../../../components/Button/Button';
import axiosInstant from '../../../../api';
import { getApiKeys } from '../../../../api/endpoints';
import { useUser } from '../../../../hooks/useUser';
import { isBrand, isBrandSales, isInternal } from '../../../../utils';
import { CreateApiKeyDialog } from './CreateApiKeyDialog';
import { DeleteApiKeyDialog } from './DeleteApiKeyDialog';
import toast from 'react-hot-toast';

interface ApiKey {
  id: string;
  key_prefix: string;
  label: string | null;
  scopes: string[];
  brand_id: number;
  created_by: number;
  last_used_at: string | null;
  created_at: string;
  brand?: { id: number; name: string };
  creator?: { userid: number; displayName: string };
}

interface ApiKeysPageProps {
  // Internal brand detail page: manage this brand's keys on its behalf.
  // Brand users never pass it — their brand comes from their membership.
  brandId?: number;
}

export default function ApiKeysPage({ brandId }: ApiKeysPageProps) {
  const { role } = useUser();
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ApiKey | null>(null);

  const fetchKeys = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axiosInstant.get(getApiKeys(brandId));
      setKeys(res.data?.data ?? res.data ?? []);
    } catch (err) {
      toast.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, [brandId]);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  const handleCreated = () => {
    fetchKeys();
  };

  const handleDeleted = () => {
    setDeleteTarget(null);
    fetchKeys();
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // The Brand column only earns its place on the all-brands list.
  const showInternalColumns = isInternal(role) && !brandId;
  // API-key management stays admin/member-only — the API's apiKeys allow-list
  // deliberately excludes 'brand sales'. Internal staff create keys only
  // from a brand's detail page, where the brand is explicit.
  const canCreate =
    (isBrand(role) && !isBrandSales(role)) || (isInternal(role) && !!brandId);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-6">
        <div className="min-w-0">
          <h3 className="text-text-1 text-xl sm:text-3xl font-bold uppercase font-[CircularStd]">
            Public API Keys
          </h3>
          <p className="text-text-2 text-[13px] sm:text-[14px] mt-1">
            Manage API keys for accessing the public product API.
          </p>
        </div>
        {canCreate && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => setShowCreateDialog(true)}
            className="!w-full sm:!w-auto !text-sm sm:!text-base"
          >
            Generate New Key
          </Button>
        )}
      </div>

      {loading ? (
        <Box className="flex justify-center py-12">
          <CircularProgress />
        </Box>
      ) : keys.length === 0 ? (
        <Box className="text-center py-12">
          <Typography className="text-muted text-[14px]">
            No API keys yet.{' '}
            {canCreate && 'Generate one to get started.'}
          </Typography>
        </Box>
      ) : (
        <>
          {/* Desktop: table */}
          <TableContainer className="hidden md:block">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Key</TableCell>
                  <TableCell>Label</TableCell>
                  {showInternalColumns && <TableCell>Brand</TableCell>}
                  <TableCell>Created By</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Last Used</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {keys.map((apiKey) => (
                  <TableRow key={apiKey.id}>
                    <TableCell>
                      <code className="text-[13px] bg-surface-2 px-2 py-1 rounded">
                        {apiKey.key_prefix}••••
                      </code>
                    </TableCell>
                    <TableCell>{apiKey.label || '—'}</TableCell>
                    {showInternalColumns && (
                      <TableCell>{apiKey.brand?.name || '—'}</TableCell>
                    )}
                    <TableCell>{apiKey.creator?.displayName || '—'}</TableCell>
                    <TableCell>{formatDate(apiKey.created_at)}</TableCell>
                    <TableCell>{formatDate(apiKey.last_used_at)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Revoke key">
                        <IconButton
                          size="small"
                          onClick={() => setDeleteTarget(apiKey)}
                          sx={{ color: 'error.main' }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Mobile: cards */}
          <div className="md:hidden flex flex-col gap-3">
            {keys.map((apiKey) => (
              <div
                key={apiKey.id}
                className="border border-border-token rounded-xl p-4 bg-surface"
              >
                <div className="flex justify-between items-start gap-2 mb-3">
                  <code className="text-[13px] bg-surface-2 px-2 py-1 rounded break-all">
                    {apiKey.key_prefix}••••
                  </code>
                  <Tooltip title="Revoke key">
                    <IconButton
                      size="small"
                      onClick={() => setDeleteTarget(apiKey)}
                      sx={{ color: 'error.main' }}
                      className="shrink-0"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </div>
                <div className="flex flex-col gap-1.5 text-[13px]">
                  <div className="flex justify-between gap-2">
                    <span className="text-muted">Label</span>
                    <span className="text-right break-all">{apiKey.label || '—'}</span>
                  </div>
                  {showInternalColumns && (
                    <div className="flex justify-between gap-2">
                      <span className="text-muted">Brand</span>
                      <span className="text-right break-all">{apiKey.brand?.name || '—'}</span>
                    </div>
                  )}
                  <div className="flex justify-between gap-2">
                    <span className="text-muted">Created By</span>
                    <span className="text-right break-all">{apiKey.creator?.displayName || '—'}</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="text-muted">Created</span>
                    <span className="text-right">{formatDate(apiKey.created_at)}</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="text-muted">Last Used</span>
                    <span className="text-right">{formatDate(apiKey.last_used_at)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <CreateApiKeyDialog
        brandId={brandId}
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onCreated={handleCreated}
      />

      {deleteTarget && (
        <DeleteApiKeyDialog
          open={!!deleteTarget}
          apiKey={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={handleDeleted}
        />
      )}
    </div>
  );
}
